import { Fetch } from '../helpers/fetchWrapper';
import { adaptGraphResponse, buildAnalyticsQueryString } from '../helpers/analyticsAdapters';
import useServiceQuery from '../hooks/useServiceQuery';

const campaignService = {
  _url: process.env.REACT_APP_CAMPAIGN_API_URL,
  _analyticsUrl: process.env.REACT_APP_CAMPAIGN_ANALYTICS_API_URL,

  GetAllCampaigns(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.all',
      params: searchQuery,
      queryFn: () => this.getAllCampaigns(searchQuery),
      refresh: fetch,
    });
    return {
      campaigns_loading: loading,
      campaigns_error: error,
      campaigns_data: data ?? { campaigns: [], totalItems: 0 },
    };
  },

  /**
   * The workspace's campaign counts (`totalCampaigns`, `totalActiveCampaigns`),
   * read by Campaigns and by the empty screens of Unibox, Tasks and Analytics.
   * `data` is null when the request failed or has not answered.
   */
  GetWorkspaceCampaignTotals(fetch, { enabled = true } = {}) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.totals',
      queryFn: () => this.getWorkspaceCampaignTotals().then(res => (res?.success && res?.data ? res.data : null)),
      refresh: fetch,
      enabled,
    });
    return {
      campaign_totals_loading: enabled && loading,
      campaign_totals_error: error,
      campaign_totals_data: data ?? null,
    };
  },

  GetAllEmails(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.emails',
      params: searchQuery,
      queryFn: () => this.getAllEmails(searchQuery),
      refresh: fetch,
    });
    return {
      emails_loading: loading,
      emails_error: error,
      emails_data: data ?? { emails: [], totalItems: 0 },
    };
  },

  GetCampaignGraphAnalytics(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.graph',
      params: searchQuery,
      queryFn: () => this.getCampaignGraphAnalytics(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery),
    });
    return {
      campaign_graph_loading: loading,
      campaign_graph_error: error,
      campaign_graph_data: data ?? { kpi: [], totalItems: 0 },
    };
  },

  GetCampaignAnalytics(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.analytics',
      params: searchQuery,
      queryFn: () => this.getCampaignAnalytics(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery),
    });
    return {
      campaign_analytics_loading: loading,
      campaign_analytics_error: error,
      campaign_analytics_data: data ?? { kpi: [], totalItems: 0 },
    };
  },

  GetCampaignLeads(searchQuery, fetch) {
    const { data, loading, error } = useServiceQuery({
      name: 'campaigns.leads',
      params: searchQuery,
      queryFn: () => this.getCampaignsLeads(searchQuery),
      refresh: fetch,
      enabled: Boolean(searchQuery?.campaignId),
    });
    return {
      campaign_leads_loading: loading,
      campaign_leads_error: error,
      campaign_leads_data: data ?? { leadsData: [], totalItems: 0 },
    };
  },


  async getAllCampaigns({
    page = 1,
    itemsPerPage = 10,
    searchText = '',
    getAll = false,
    startDate = '',
    endDate = '',
    status = 'all',
    orderBy = 'newest_first',
    sentSince = '',
  }) {
    let res = await Fetch.get(
      `${this._url}/campaigns/?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}&status=${status}&orderBy=${orderBy}&sentSince=${sentSince}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignsLeads({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, campaignId }) {
    let res = await Fetch.get(
      `${this._url}/get-campaign-leads/${campaignId}?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async addCampaignEnrichment(payload) {
    let res = await Fetch.post(`${this._url}/add-campaign-enrichment`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsToCompaign(payload) {
    let res = await Fetch.post(`${this._url}/add-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async scheduleSettingCampaign(payload) {
    let res = await Fetch.post(`${this._url}/schedule-setting-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addSequenceToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-sequence-list-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllEmails({ page = 1, itemsPerPage = 10, searchText = '', getAll = false, startDate = '', endDate = '' }) {
    let res = await Fetch.get(
      `${this._url}/get-all-email-accounts?page=${page}&itemsPerPage=${itemsPerPage}&searchText=${searchText}&getAll=${getAll}&startDate=${startDate}&endDate=${endDate}`,
    );
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async addEmailAccountsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-email-account-list-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCampaignDetail(id) {
    let res = await Fetch.get(`${this._url}/campaigns/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCampaignActivities(id) {
    let res = await Fetch.get(`${this._url}/campaigns/${id}/activities`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCampaignAnalytics(id) {
    let res = await Fetch.get(`${this._analyticsUrl}/campaigns/${id}/stats`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignGraphAnalytics(id, query = {}) {
    const queryString = buildAnalyticsQueryString(query);
    let res = await Fetch.get(`${this._analyticsUrl}/campaigns/${id}/graph?${queryString}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return adaptGraphResponse(res);
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something Went Wrong');
  },

  async getCampaignSequences(id) {
    let res = await Fetch.get(`${this._url}/get-campaign-sequences/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignScheduleSettings(id) {
    let res = await Fetch.get(`${this._url}/get-schedule-settings/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async getCampaignEmailAccounts(id) {
    let res = await Fetch.get(`${this._url}/get-email-accounts/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getAllSequence(id) {
    let res = await Fetch.get(`${this._url}/get-all-sequences/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getEnrichLeadsColumn(payload) {
    let res = await Fetch.post(`${this._url}/get-enrich-leads-columns`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addHoudinyLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-houdiny-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getCompanyDetails(website) {
    let res = await Fetch.post(`${this._url}/get-company-info`, { website });
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async resumeCampaign(id) {
    let res = await Fetch.patch(`${this._url}/resume-paused-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteCampaign(id) {
    let res = await Fetch.delete(`${this._url}/campaigns/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async deleteCampaignSequence(payload) {
    let res = await Fetch.delete(`${this._url}/delete-campaign-sequence`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async renameCampaign(payload) {
    let res = await Fetch.patch(`${this._url}/rename-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async pauseCampaign(id) {
    let res = await Fetch.patch(`${this._url}/pause-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async addMoreLeadsToCampaign(id, payload) {
    let res = await Fetch.post(`${this._url}/add-more-leads-to-campaign/${id}`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async unSubscribeCampaign(token) {
    let res = await Fetch.get(`${this._url}/campaign/${token}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async downloadCampaignAnalyticsCsv(id) {
    let res = await Fetch.get(`${this._url}/get-analytics-report/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addNewLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-new-houdiny-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsFromSublistToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-list-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addUserLeadToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-user-lead-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async previewSublistLeadsToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/preview-sublist-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getUserLeadsPreview(payload) {
    let res = await Fetch.post(`${this._url}/preview-user-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },



  async addLeadsFromJobToCampaign(payload) {
    let res = await Fetch.post(`${this._url}/add-job-leads-to-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async getWorkspaceCampaignTotals() {
    let res = await Fetch.get(`${this._url}/campaigns/totals`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },
  async AiCampaign(payload) {
    let res = await Fetch.post(`${this._url}/ai-campaign`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async addLeadsToWorkflow(workflowId, payload) {
    let res = await Fetch.post(`${this._url}/workflows/${workflowId}/add-leads`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async importWorkflowLeads(payload) {
    let res = await Fetch.post(`${this._url}/leads/import`, payload);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

  async GetAiCampaign(id) {
    let res = await Fetch.get(`${this._url}/ai-campaign/${id}`);
    if (res.status >= 200 && res.status < 300) {
      res = await res.json();
      return res;
    }
    const { message } = await res.json();
    throw new Error(message ?? 'Something went wrong');
  },

};

export default campaignService;
